import { useState, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { registerSchema, type RegisterInput } from '@tcrd/shared';
import { clearDriverCode, storedDriverCode } from '../../lib/driver-invite';
import { GoogleButton } from '../../components/GoogleButton';
import { Button, Notice, TextField } from '@tcrd/ui';
import { api, errorMessage } from '../../lib/api';
import { useAuth } from '../../stores/auth';
import { AuthLayout } from './AuthLayout';

type AccountType = 'cliente' | 'socio';

export function RegisterPage() {
  const registerUser = useAuth((s) => s.register);
  const history = useHistory();
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const next = params.get('next');
  // El enlace de un conductor siempre registra clientes.
  const [type, setType] = useState<AccountType>(params.get('tipo') === 'socio' && !storedDriverCode() ? 'socio' : 'cliente');
  const [applied, setApplied] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { register, handleSubmit, formState } = useForm<RegisterInput>({
    resolver: zodResolver(registerSchema),
    defaultValues: { phone: '+1809' },
  });

  const onSubmit = handleSubmit(async (values) => {
    setError(null);
    try {
      if (type === 'socio') {
        // Queda pendiente: el administrador debe aprobarla antes de poder entrar.
        await api('/auth/register-driver', { body: values });
        setApplied(values.email);
        return;
      }
      // Registro desde el enlace de un conductor: el cliente queda con ese conductor.
      const code = storedDriverCode();
      await registerUser({ ...values, driverCode: code });
      clearDriverCode();
      // Sin enlace: primero elige a su conductor de confianza (QR, código o lista).
      if (!code) return void history.replace('/app/my-driver');
      const safeNext = next && next.startsWith('/') && !next.startsWith('//') ? next : null;
      history.replace(safeNext ?? '/app/profile?welcome=1');
    } catch (e) {
      setError(errorMessage(e));
    }
  });

  // "Continuar con Google": crea la cuenta (o entra si ya existe) sin llenar el formulario.
  const loginWithGoogle = useAuth((s) => s.loginWithGoogle);
  const onGoogle = useCallback(
    async (credential: string) => {
      setError(null);
      const user = await loginWithGoogle(credential, storedDriverCode());
      clearDriverCode();
      const safeNext = next && next.startsWith('/') && !next.startsWith('//') ? next : null;
      history.replace(safeNext ?? (user.roles.includes('CUSTOMER') ? '/app/home' : '/'));
    },
    [loginWithGoogle, history, next],
  );

  if (applied) {
    return (
      <AuthLayout title="¡Solicitud enviada!" subtitle="Gracias por querer ser socio conductor.">
        <div className="flex flex-col gap-4">
          <Notice>
            Revisaremos tu solicitud y te avisaremos a <strong>{applied}</strong> cuando sea aprobada. Después podrás iniciar sesión y subir tus documentos y tu vehículo.
          </Notice>
          <Link to="/" className="tap w-full h-12 rounded-xl bg-black text-white dark:bg-white dark:text-black font-semibold flex items-center justify-center">
            Volver al inicio
          </Link>
        </div>
      </AuthLayout>
    );
  }

  const err = formState.errors;
  const socio = type === 'socio';
  const tab = (t: AccountType, label: string) => (
    <button
      type="button"
      role="tab"
      aria-selected={type === t}
      onClick={() => (setType(t), setError(null))}
      className={`tap flex-1 h-11 rounded-lg text-sm font-semibold transition-colors ${type === t ? 'bg-[var(--surface)] shadow-sm' : 'text-[var(--text-muted)]'}`}
    >
      {label}
    </button>
  );

  return (
    <AuthLayout
      title={socio ? 'Sé socio conductor' : 'Crea tu cuenta'}
      subtitle={socio ? 'Maneja con nosotros y atiende a tus propios clientes.' : 'Solicita viajes, sigue a tu conductor y guarda tus comprobantes.'}
      footer={
        <>
          ¿Ya tienes cuenta?{' '}
          <Link to={`/login${search}`} className="font-semibold underline">
            Inicia sesión
          </Link>
        </>
      }
    >
      {!storedDriverCode() && (
        <div role="tablist" aria-label="Tipo de cuenta" className="mb-5 flex gap-1 rounded-xl bg-[var(--surface-2)] p-1">
          {tab('cliente', 'Cliente')}
          {tab('socio', 'Socio conductor')}
        </div>
      )}
      <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
        {error && <Notice tone="danger">{error}</Notice>}
        <div className="grid grid-cols-2 gap-3">
          <TextField label="Nombre" autoComplete="given-name" error={err.firstName?.message} {...register('firstName')} />
          <TextField label="Apellido" autoComplete="family-name" error={err.lastName?.message} {...register('lastName')} />
        </div>
        <TextField label="Teléfono (WhatsApp)" type="tel" autoComplete="tel" inputMode="tel" hint="Formato +1 809/829/849 seguido del número" error={err.phone?.message} {...register('phone')} />
        <TextField label="Correo electrónico" type="email" autoComplete="email" inputMode="email" error={err.email?.message} {...register('email')} />
        <TextField label="Contraseña" type="password" autoComplete="new-password" hint="Mínimo 10 caracteres, con letras y números" error={err.password?.message} {...register('password')} />
        <label className="flex items-start gap-3 text-sm tap">
          <input type="checkbox" className="mt-1 size-5 accent-[#E5252A]" {...register('acceptTerms')} />
          <span>
            Acepto los <Link to="/legal/terminos" className="underline">términos del servicio</Link> y la{' '}
            <Link to="/legal/privacidad" className="underline">política de privacidad</Link>.
          </span>
        </label>
        {err.acceptTerms && <p className="text-xs text-brand-red -mt-2">{err.acceptTerms.message}</p>}
        {socio && <p className="text-xs text-[var(--text-muted)]">Tu cuenta quedará pendiente hasta que el administrador la apruebe.</p>}
        <Button type="submit" size="lg" block loading={formState.isSubmitting}>
          {socio ? 'Enviar solicitud' : 'Crear cuenta'}
        </Button>
      </form>
      {!socio && (
        <div className="mt-5 flex flex-col gap-4">
          <GoogleButton onCredential={onGoogle} onError={setError} />
        </div>
      )}
    </AuthLayout>
  );
}
